import React from "react";
import {
    Grid, Card, CardHeader, CardContent, CardActions, Divider,
    IconButton, Button, Stack, Typography, Alert, Box, Paper, Tooltip
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddIcon from "@mui/icons-material/Add";
import {FieldRenderer} from "./FormGenerator";
import {Trans, useTranslation} from "react-i18next";

const defaultValueFor = (type) => {
    if (type === "addMultiple") return [];
    if (type === "addSingle") return {};
    if (type === "number") return "";
    return "";
};

const makeEmptyItem = (fields) =>
    (fields || []).reduce((acc, f) => {
        acc[f.id] = defaultValueFor(f.type);
        return acc;
    }, {});

export default function AddMultipleInput({
                                             field,
                                             value,
                                             onChange,
                                             parentSection,
                                             reportValidity, // ⬅️ added
                                         }) {
    const { t } = useTranslation();

    const items = Array.isArray(value) ? value : [];
    const subFields = field.fields || [];

    // --- Plan limits (replace with real plan) ---
    const planType = 1 ?? 1;
    const planCap = planType === 1 ? 1 : 3;
    const canAddMore = items.length < planCap;

    const update = (next) => onChange(field.id, next, parentSection);
    const updateItem = (idx, key, val) => {
        const next = items.map((it, i) => (i === idx ? {...it, [key]: val} : it));
        update(next);
    };
    const addItem = () => {
        if (!canAddMore) return;
        update([...(items || []), makeEmptyItem(subFields)]);
    };

    const minItems = 1;
    const canRemove = items.length > minItems;
    const removeItem = (idx) => {
        if (!canRemove) return;
        update(items.filter((_, i) => i !== idx));
    };

    return (
        <Stack spacing={2}>
            {/* Section header row */}
            {planType === 1 && (
                <Alert severity="info" variant="outlined" sx={{borderRadius: 2}}>
                    <Trans i18nKey="userInformation.freemium.info"/>
                </Alert>
            )}

            {items.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 2,
                    }}
                >
                    <Button
                        onClick={addItem}
                        startIcon={<AddIcon />}
                        variant="contained"
                        size="small"
                        disabled={!canAddMore}
                    >
                        {t(field.addPrompt)|| "Add item"}
                    </Button>
                </Box>
            )}

            {items.length === 0 ? (
                <Paper
                    variant="outlined"
                    sx={{
                        px: 3,
                        py: 5,
                        textAlign: "center",
                        color: "text.secondary",
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="body1" sx={{mb: 2}}>
                        You can add another user to help you manage your company data.
                    </Typography>
                    <Button
                        onClick={addItem}
                        startIcon={<AddIcon/>}
                        variant="contained"
                        size="small"
                        disabled={!canAddMore}
                    >
                        {t(field.addPrompt) || "Add your first item"}
                    </Button>
                </Paper>
            ) : (
                <>
                    <Grid container spacing={2}>
                        {items.map((item, idx) => (
                            <Grid key={idx} item xs={12}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        position: "relative",
                                        transition: "box-shadow 200ms ease",
                                        "&:hover": {boxShadow: 3},
                                    }}
                                >
                                    <CardHeader
                                        sx={{
                                            pb: 0.5,
                                            "& .MuiCardHeader-action": {alignSelf: "center", mt: 0},
                                        }}
                                        title={
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1.2}}>
                                                <Box
                                                    sx={{
                                                        bgcolor: "grey.200",
                                                        color: "text.primary",
                                                        borderRadius: "6px",
                                                        fontSize: 13,
                                                        px: 1.5,
                                                        py: 0.5,
                                                        fontWeight: 600,
                                                        lineHeight: 1.6,
                                                        minWidth: 28,
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {idx + 1}
                                                </Box>
                                                <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                                                    {field.itemTitle || "User"}
                                                </Typography>
                                            </Box>
                                        }
                                        action={
                                            <Tooltip title="Remove item">
                                                <IconButton
                                                    disabled={!canRemove}
                                                    aria-label="remove"
                                                    onClick={() => removeItem(idx)}
                                                    size="small"
                                                    color="error"
                                                    sx={{
                                                        opacity: {xs: 1, md: 0.65},
                                                        transition: "opacity 150ms",
                                                        "&:hover": {opacity: 1},
                                                    }}
                                                >
                                                    <CloseRoundedIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    />

                                    <Divider sx={{my: 1}}/>

                                    <CardContent sx={{pt: 1, pb: 2}}>
                                        <Grid container spacing={2}>
                                            {subFields.map((subField, colIdx) => (
                                                <Grid
                                                    key={`${subField.id}-${idx}`}
                                                    item
                                                    xs={12}
                                                    md={6}
                                                >
                                                    <FieldRenderer
                                                        field={subField}
                                                        value={item?.[subField.id]}
                                                        onChange={(childFieldId, newVal) =>
                                                            updateItem(idx, childFieldId, newVal)
                                                        }
                                                        index={colIdx + 1}
                                                        parentSection={parentSection}
                                                        childSection={field.id}
                                                        reportValidity={reportValidity} // ⬅️ added
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
        </Stack>
    );
}
